import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { qtyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DocPartyStrip } from "@/modules/docs/DocPartyStrip";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DeskDocActionRail } from "@/modules/shell/DeskDocActionRail";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { RemindMeButton } from "@/modules/shell/RemindMeButton";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import {
  getPurchaseShipment,
  submitPurchaseShipment,
  voidPurchaseShipment,
} from "@/shared/api/purchasing";
import { notify } from "@/shared/feedback/notify";

const SHIP_DOC_TABS = ["shipment", "lines"] as const;

export function PurchaseShipmentDetailPage() {
  const { companySlug, shipmentId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getPurchaseShipment>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [docTab, setDocTab] = useDeskDocTab("shipment", SHIP_DOC_TABS);

  async function reload() {
    if (!companySlug || !shipmentId) return;
    const d = await getPurchaseShipment(companySlug, shipmentId);
    setData(d);
  }

  useEffect(() => {
    let cancelled = false;
    reload().catch((err: unknown) => {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : "Failed";
      if (msg === "Not signed in") navigate("/login", { replace: true });
      else setError(msg);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, shipmentId, navigate]);

  useRecordRecentDoc({
    companySlug,
    kind: "purchaseShipment",
    id: data?.shipment.id,
    label: data?.shipment.number,
    path:
      companySlug && data?.shipment.id
        ? `/c/${companySlug}/purchasing/in-transit/${data.shipment.id}`
        : undefined,
    hint: data?.shipment.supplierName,
  });

  if (!companySlug || !shipmentId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading…</Alert>
        )}
      </PageFrame>
    );
  }

  const { shipment, lines } = data;

  async function onSubmit() {
    setBusy(true);
    setError(null);
    try {
      await submitPurchaseShipment(companySlug!, shipmentId!);
      notify.success("Submitted");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Submit failed");
    } finally {
      setBusy(false);
    }
  }

  async function onVoid() {
    const ok = await confirmUi({
      title: `Void ${shipment.number}?`,
      description: "Frees qty for other inbound shipments. Receive path unchanged.",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await voidPurchaseShipment(companySlug!, shipmentId!);
      notify.success("Voided");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <div className="mb-3">
        <DeskDocBackLink
          listBasePath={`/c/${companySlug}/purchasing/in-transit`}
          listLabel="In transit"
        />
      </div>
      <PageHeader
        eyebrow="In transit"
        title={shipment.number}
        description={[shipment.supplierName, "logistics only (no stock yet)"]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <div className="flex flex-wrap gap-2">
            <RemindMeButton
              companySlug={companySlug}
              kind="purchase_shipment"
              docId={shipmentId}
              docNumber={shipment.number}
              partyName={shipment.supplierName}
              disabled={busy}
            />
            {shipment.lifecycle !== "void" ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void onVoid()}
              >
                Void
              </Button>
            ) : null}
          </div>
        }
      />
      <div className="mt-2">
        <LifecycleStatus value={shipment.lifecycle} showLabel />
      </div>
      <DocPartyStrip
        companySlug={companySlug}
        partyId={shipment.supplierId}
        partyName={shipment.supplierName}
        roleLabel="Supplier"
        primaryPhone={shipment.supplierPrimaryPhone}
        primaryEmail={shipment.supplierPrimaryEmail}
        primaryAddress={shipment.supplierPrimaryAddress}
        partyNotes={shipment.supplierNotes}
        docNotes={shipment.notes}
      />
      <TagsAssigneesPanel
        companySlug={companySlug}
        docType="purchase_shipment"
        docId={shipmentId}
        linkPath={`/c/${companySlug}/purchasing/in-transit/${shipmentId}`}
        docLabel={[shipment.number, shipment.supplierName]
          .filter(Boolean)
          .join(" · ")}
      />
      {shipment.lifecycle === "draft" ? (
        <DeskDocActionRail className="mt-4">
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => void onSubmit()}
          >
            Submit
          </Button>
        </DeskDocActionRail>
      ) : null}
      {shipment.lifecycle === "open" ? (
        <DeskDocActionRail className="mt-4">
          <Button
            type="button"
            size="sm"
            onClick={() =>
              navigate(
                `/c/${companySlug}/purchasing/orders/${shipment.purchaseOrderId}`,
              )
            }
          >
            Receive from order
          </Button>
        </DeskDocActionRail>
      ) : null}
      <DeskDocTabs
        aria-label="In transit views"
        value={docTab}
        onChange={setDocTab}
        items={[
          { id: "shipment", label: "Shipment" },
          { id: "lines", label: "Lines", count: lines.length },
        ]}
      />
      {error ? (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      ) : null}
      {docTab === "shipment" && (
        <FieldGroup title="Summary" className="mt-6" level="sub">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-[var(--color-muted)]">Purchase order</dt>
            <dd className="m-0">
              <RelatedDocLink
                companySlug={companySlug}
                kind="purchase_order"
                docId={shipment.purchaseOrderId}
              >
                {shipment.purchaseOrderNumber}
              </RelatedDocLink>
            </dd>
            <dt className="text-[var(--color-muted)]">Doc date</dt>
            <dd className="m-0">{shipment.docDate}</dd>
          </dl>
        </FieldGroup>
      )}
      {docTab === "lines" && (
        <div className="mt-4">
          {lines.length === 0 ? (
            <EmptyState
              title="No lines"
              description="This shipment has no product lines."
            />
          ) : (
            <ul className="m-0 list-none space-y-2 border-t border-[var(--color-border)]/90 p-0 pt-4">
              {lines.map((l) => (
                <li key={l.id} className="flex justify-between gap-3 text-sm">
                  <span>
                    {l.productName}
                    {l.description ? (
                      <span className="block text-xs text-[var(--color-muted)]">
                        {l.description}
                      </span>
                    ) : null}
                  </span>
                  <span className="font-mono tabular-nums">
                    {qtyDisplay(l.qty)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <DocActivity
        companySlug={companySlug}
        docType="purchase_shipment"
        docId={shipmentId}
        linkPath={`/c/${companySlug}/purchasing/in-transit/${shipmentId}`}
      />
    </PageFrame>
  );
}
